import { useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../contexts/UserContext';
// Fjernet ubrukte date-fns imports
import { useQuery } from '@tanstack/react-query';

interface HeroSectionProps {
  className?: string;
}

// Fjernet ubrukt Residence-interface

export function HeroSection({ className }: HeroSectionProps) {
  const { currentResidence, residences, setCurrentResidence } = useUser();
  const residenceId = currentResidence?.residence_id;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Filtrer residences til unike bofellesskap (én per residence_id)
  // Prioriterer den med leilighetsnummer hvis tilgjengelig
  const uniqueResidences = useMemo(() => {
    if (!residences || residences.length === 0) return [];
    
    const residenceMap = new Map<number, typeof residences[0]>();
    
    residences.forEach(residence => {
      const existing = residenceMap.get(residence.residence_id);
      
      // Hvis vi ikke har noen for dette residence_id, eller hvis denne har leilighetsnummer og den eksisterende ikke har
      if (!existing || (residence.apartment?.number && !existing.apartment?.number)) {
        residenceMap.set(residence.residence_id, residence);
      }
    });
    
    return Array.from(residenceMap.values());
  }, [residences]);
  
  const hasMultipleResidences = uniqueResidences.length > 1;

  // stripped render logging

  // React Query: hero image per residence
  const { data: coverImageUrl } = useQuery({
    queryKey: ['residence', residenceId, 'hero-image'],
    enabled: Boolean(residenceId),
    queryFn: async (): Promise<string | null> => {
      if (!residenceId) return null;
      const { data, error } = await supabase
        .from('user_data_view')
        .select('cover_image')
        .eq('residence_id', residenceId)
        .limit(1);
      if (error || !data?.[0]?.cover_image) return null;
      const imageUrl = data[0].cover_image.startsWith('http')
        ? data[0].cover_image
        : supabase.storage
            .from('residence_images')
            .getPublicUrl(data[0].cover_image)
            .data.publicUrl;
      return imageUrl;
    }
  });

  if (!currentResidence) {
    return null;
  }

  return (
    <section 
      className={cn(
        'relative h-[70vh] min-h-[500px] w-full overflow-hidden',
        className
      )}
      aria-labelledby="hero-heading"
    >
      <div 
        className={cn(
          "absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-500",
          imageError || !coverImageUrl ? 'opacity-0' : 'opacity-100'
        )}
        style={{
          backgroundImage: imageError || !coverImageUrl
            ? 'url(/images/hero-condo.jpg)'
            : `url(${coverImageUrl})`,
          opacity: imageLoaded ? 1 : 0
        }}
        role="img"
        aria-label="Bilde av bygningen"
      >
        {coverImageUrl && !imageError && (
          <img 
            src={coverImageUrl} 
            alt="" 
            className="hidden" 
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}
      </div>
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 flex h-full flex-col items-center justify-center px-4 text-center text-white">
        {/* Bofellesskapsvelger - erstatter h1 når bruker har flere residences */}
        {hasMultipleResidences ? (
          <div className="w-full max-w-4xl">
            <div className="relative">
              <style>{`
                #residence-selector {
                  font-size: clamp(1.875rem, 4vw, 3.75rem);
                  -webkit-appearance: none;
                  -moz-appearance: none;
                  appearance: none;
                  padding: 0 !important;
                  margin: 0 !important;
                  height: auto !important;
                  line-height: 1.2 !important;
                }
                #residence-selector::-webkit-appearance {
                  -webkit-appearance: none;
                }
                #residence-selector::-ms-expand {
                  display: none !important;
                }
                #residence-selector option {
                  font-size: 0.875rem !important;
                  background-color: rgb(17, 24, 39) !important;
                  color: rgb(245, 245, 240) !important;
                  padding: 0 !important;
                  margin: 0 !important;
                }
              `}</style>
              <select
                id="residence-selector"
                value={currentResidence?.residence_id.toString() || ''}
                onChange={(e) => {
                  const selectedResidenceId = parseInt(e.target.value, 10);
                  // Finn første tilhørighet for dette bofellesskapet
                  const selectedResidence = residences.find(r => r.residence_id === selectedResidenceId);
                  if (selectedResidence) {
                    setCurrentResidence(selectedResidence);
                  }
                }}
                className={cn(
                  'w-full bg-transparent backdrop-blur-sm text-condo-light border-2 border-white/30',
                  'font-bold text-center rounded-lg cursor-pointer',
                  'appearance-none transition-all',
                  'focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50'
                )}
                style={{
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  backgroundImage: 'none',
                  height: 'auto',
                  padding: 0,
                  margin: 0,
                  paddingRight: '3rem',
                  lineHeight: '1.2'
                }}
                aria-label="Velg bofellesskap"
              >
                {uniqueResidences.map(r => (
                  <option 
                    key={r.residence_id} 
                    value={r.residence_id.toString()}
                  >
                    {r.residence.name}
                  </option>
                ))}
              </select>
              <ChevronDown 
                className="absolute right-4 top-1/2 -translate-y-1/2 h-6 w-6 md:h-8 md:w-8 lg:h-10 lg:w-10 xl:h-12 xl:w-12 text-condo-light pointer-events-none"
                aria-hidden="true"
              />
            </div>
          </div>
        ) : (
          <h1 className="mb-4 text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-condo-light">
            {currentResidence.residence.name}
          </h1>
        )}
        <p className="mb-0 max-w-2xl text-base md:text-lg lg:text-xl">
          Velkommen til {currentResidence.residence.name}. Her finner du all informasjon om borettslaget ditt.
        </p>
      </div>
    </section>
  );
}